import { useState } from 'react';
import { CheckCheck, Plus, SearchX } from 'lucide-react';
import { toast } from 'sonner';
import type { Task } from '@underline/shared';
import { Button } from '@/components/ui/button';
import { SearchField } from '@/components/ui/search-field';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { TaskCard } from '@/components/tasks/task-card';
import { SwipeActions } from '@/components/ui/swipe-actions';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonList } from '@/components/ui/skeleton-card';
import { TaskViewModal } from '@/components/tasks/task-view-modal';
import { useDeleteTask, useTasks, useToggleTask } from '@/hooks/use-tasks';
import { useUsers } from '@/hooks/use-users';
import { useUiStore } from '@/stores/ui-store';
import { TypedHeading } from '@/components/ui/typed-heading';
import { isOverdue } from '@/lib/dates';

type Filter = 'all' | 'pending' | 'done';

export function MarketingPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const { data: tasks = [], isLoading } = useTasks();
  const { data: users = [] } = useUsers();
  const toggle = useToggleTask();
  const del = useDeleteTask();
  const openNewTask = useUiStore((s) => s.openNewTask);
  const openEditTask = useUiStore((s) => s.openEditTask);
  const [viewing, setViewing] = useState<Task | null>(null);

  // Contagens por status (rotulos do segmented + subtitulo do header)
  const counts = {
    all: tasks.length,
    pending: tasks.filter((t) => t.status === 'pending').length,
    done: tasks.filter((t) => t.status === 'done').length,
  };

  // Filtro (status + busca por titulo) + ordenacao:
  // atrasados pendentes primeiro, depois pendentes, depois por data.
  const filtered = tasks
    .filter((t) => {
      if (filter === 'pending' && t.status !== 'pending') return false;
      if (filter === 'done' && t.status !== 'done') return false;
      if (q && !t.title.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const ao = isOverdue(a.due_date, a.due_time) && a.status === 'pending' ? 0 : 1;
      const bo = isOverdue(b.due_date, b.due_time) && b.status === 'pending' ? 0 : 1;
      if (ao !== bo) return ao - bo;
      if (a.status !== b.status) return a.status === 'pending' ? -1 : 1;
      return `${a.due_date} ${a.due_time ?? ''}`.localeCompare(`${b.due_date} ${b.due_time ?? ''}`);
    });

  return (
    <div>
      {/* Header: titulo + subtitulo com contagem (pluralizada) */}
      <header className="mb-5">
        <TypedHeading text="Posts" />
        <p className="mt-2 text-sm text-muted-foreground">
          {counts.pending} pendente{counts.pending !== 1 ? 's' : ''} · {counts.done} feito
          {counts.done !== 1 ? 's' : ''}
        </p>
      </header>

      {/* Busca por titulo */}
      <div className="mb-3">
        <SearchField value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar post…" />
      </div>

      {/* Filtro de status com contagens */}
      <SegmentedControl
        value={filter}
        onChange={(v) => setFilter(v)}
        options={[
          { value: 'all', label: `Todos · ${counts.all}` },
          { value: 'pending', label: `Pendentes · ${counts.pending}` },
          { value: 'done', label: `Feitos · ${counts.done}` },
        ]}
        className="mb-5"
      />

      {isLoading ? (
        <SkeletonList count={4} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={q ? <SearchX size={20} /> : <CheckCheck size={20} />}
          title={q ? 'Nada encontrado' : filter === 'done' ? 'Nenhum post feito' : 'Tudo em dia'}
          description={q ? `Sem resultados para "${q}".` : 'Nada por aqui.'}
          action={
            <Button onClick={openNewTask}>
              <Plus size={15} />
              Nova postagem
            </Button>
          }
        />
      ) : (
        // Lista vertical (mobile-first), com entrada escalonada u-rise por item
        <div className="flex flex-col gap-2.5">
          {filtered.map((t, i) => {
            const responsible = users.find((u) => u.id === t.responsible_id);
            return (
              <div
                key={t.id}
                style={{ animation: `u-rise 0.3s var(--ease-underline) ${i * 0.04}s both` }}
              >
                <SwipeActions
                  onComplete={() => toggle.mutate(t.id)}
                  onDelete={() => {
                    if (confirm('Excluir post?')) {
                      del.mutate(t.id, {
                        onSuccess: () => toast.success('Post excluído'),
                        onError: (err) => toast.error(err.message),
                      });
                    }
                  }}
                >
                  <TaskCard
                    task={t}
                    responsible={responsible}
                    onToggle={() => toggle.mutate(t.id)}
                    onClick={() => setViewing(t)}
                  />
                </SwipeActions>
              </div>
            );
          })}
        </div>
      )}

      <TaskViewModal
        open={!!viewing}
        task={viewing}
        onClose={() => setViewing(null)}
        onEdit={() => {
          const t = viewing;
          setViewing(null);
          if (t) setTimeout(() => openEditTask(t), 250);
        }}
      />
    </div>
  );
}
